import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import RoomStatisticsData from '../../../types/roomStatistics/RoomStatisticsData'
import { useTranslation } from 'react-i18next'
import { SaveDataState } from '../../../redux/reducers/getSaveData/saveDataReducer'
import { getProfileAttributes, putProfileAttributes, resetGetProfileAttributes } from '../../../redux/actions/profile/profile'
import { ProfileOverviewState } from '../../../redux/reducers/getProfile/getProfileOverviewReducer'
import { SaveResultState } from '../../../redux/reducers/getSaveData/saveResultReducer'
import { ProfileAttributesState } from '../../../redux/reducers/getProfile/profileAttributes/getAttributesReducer'
import updateProperties from '../../../utils/DataModifier/DataModifier'
import { useFormErrorContext } from '../../../utils/context/FormErrorContext'
import { useNavigation } from '../../../utils/context/NavigationContext'
import ErrorMessages from '../../../components/common/ErrorMessages/ErrorMessages'
import IncomeAttributeLookupObject from '../../../types/lookups/IncomeAttributeLookupObject'
import "./RoomStatistics.css";
import { ControlledAccordion, useAccordionProvider } from '@szhsin/react-accordion'
import AccordionItem, { DefaultAccordionProviderOptions } from '../../../components/common/AccordionItem/AccordionItem'
import RoomStatisticsTab from './RoomStatisticsTab'
import { accordionDisplay, getCurrentCampaignYear, getLastYearSubmittedCampaign, shouldDisplayPreviousTwoYearAttributes, shouldDisplayPreviousYearAttributes } from '../../../utils/previousIncomeAttributes'
import PreviousCampaignIncomeAttributes from '../../../components/common/PreviousCampaignIncomeAttributes/PreviousCampaignIncomeAttributes'
import { GET_PREVIOUS_PROFILE_ATTRIBUTES } from '../../../redux/actionTypes/actionsTypes'
import { selectPreviousProfileAttributes, selectProfileGetAttributes, selectProfileGetOverview, selectSaveData, selectSaveResult } from '../../../redux/selectors/exportSelectorPaths'

function RoomStatistics() {
    const dispatch = useDispatch();
    const saveData: SaveDataState = useSelector(selectSaveData);
    const saveResultState: SaveResultState = useSelector(selectSaveResult);
    const profileOverview: ProfileOverviewState = useSelector(selectProfileGetOverview);
    const profileAttributes: ProfileAttributesState = useSelector(selectProfileGetAttributes)
    const previousProfileAttributes: ProfileAttributesState = useSelector(selectPreviousProfileAttributes)
    const currentSnapshotId = Number(sessionStorage.getItem("currentSnapshotId") as string)
    const providerValue = useAccordionProvider(DefaultAccordionProviderOptions)
    const { clearErrors, errors } = useFormErrorContext()
    const { direction } = useNavigation()
    const initialRoomStatisticsData: RoomStatisticsData = {
        "numberOfRoom": null,
        "roomAvailable": null,
        "roomSold": null,
        "occupancyPercent": null,
        "averageDailyRate": null,
    }
    const [roomData, setRoomData] = useState<RoomStatisticsData>(initialRoomStatisticsData as RoomStatisticsData)
    const { t } = useTranslation('room_statistics');
    const isInternal = (sessionStorage.getItem("isInternalUser") as string) === "true"

    const roomStatisticsFragmentData: IncomeAttributeLookupObject[] = [
        {
            activeFlag: true,
            incomeAttributeDescription: t("roomNumOfRoomHeader"),
            incomeAttributeFrenchDescription: t("roomNumOfRoomHeader"),
            incomeAttributeTypeCode: "0",
            incomeAttributeCode: "numberOfRoom",
            tooltip: profileOverview.profile.profile_type_code === "HTL" ? t("roomTitleNumOfRoomHotel") : t("roomTitleNumOfRoomMotel")
        },
        {
            activeFlag: true,
            incomeAttributeDescription: t("roomRoomsAvailableHeader"),
            incomeAttributeFrenchDescription: t("roomRoomsAvailableHeader"),
            incomeAttributeTypeCode: "0",
            incomeAttributeCode: "roomAvailable",
            tooltip: t("roomTitleRoomsAvailable")
        },
        {
            activeFlag: true,
            incomeAttributeDescription: t("roomRoomsSoldHeader"),
            incomeAttributeFrenchDescription: t("roomRoomsSoldHeader"),
            incomeAttributeTypeCode: "0",
            incomeAttributeCode: "roomSold",
            tooltip: t("roomTitleRoomsSold")
        },
        {
            activeFlag: true,
            incomeAttributeDescription: t("roomOccupancyHeader"),
            incomeAttributeFrenchDescription: t("roomOccupancyHeader"),
            incomeAttributeTypeCode: "0",
            incomeAttributeCode: "occupancyPercent",
            tooltip: profileOverview.profile.profile_type_code === "HTL" ? t("roomTitleOccupancyHotel") : t("roomTitleOccupancyMotel")
        },
        {
            activeFlag: true,
            incomeAttributeDescription: t("roomAvgDailyHeader"),
            incomeAttributeFrenchDescription: t("roomAvgDailyHeader"),
            incomeAttributeTypeCode: "0",
            incomeAttributeCode: "averageDailyRate",
            tooltip: profileOverview.profile.profile_type_code === "HTL" ? t("roomTitleAvgDailyHotel") : t("roomTitleAvgDailyMotel")
        },
    ]

    const handleRoomStatisticsChange = (newAmt: number | undefined | null, name: string) => {
        setRoomData(oldRoomData => ({
            ...oldRoomData,
            [name]: newAmt === undefined ? null : newAmt
        }))
    }

    useEffect(() => {
        dispatch(resetGetProfileAttributes())
        dispatch(getProfileAttributes(profileOverview.profile.profile_type_code, currentSnapshotId))
        const campaign = getLastYearSubmittedCampaign(profileOverview.profile)

        if (campaign) {
            dispatch(getProfileAttributes(profileOverview.profile.profile_type_code, campaign.currentSnapshotId, GET_PREVIOUS_PROFILE_ATTRIBUTES))
        }
        // eslint-disable-next-line
    }, [])

    useEffect(() => {
        if (!profileAttributes.loading && profileAttributes.data) {
            const initData = { ...initialRoomStatisticsData }
            updateProperties(initData, profileAttributes.data as Record<string, number | null>)
            setRoomData(initData)
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [profileAttributes.data, profileAttributes.loading])

    useEffect(() => {
        if (saveData.saving) {
            clearErrors()
            const data = { ...profileAttributes.data, ...roomData } as Record<string, unknown>
            const triggerSpectral = isInternal || direction !== "backward"

            dispatch(putProfileAttributes(profileOverview.profile.profile_type_code, currentSnapshotId, {
                "profile_attributes": data,
                "spectral_section": "room_stats",
                "trigger_spectral": triggerSpectral,
                "is_internal": isInternal
            }))
        }
    }, [currentSnapshotId, dispatch, profileAttributes.data, profileOverview.profile.profile_type_code, roomData, saveData.saving, direction, clearErrors, isInternal])

    useEffect(() => {
        if (saveResultState.status === 200) {
            dispatch(getProfileAttributes(profileOverview.profile.profile_type_code, currentSnapshotId));
        }
    }, [currentSnapshotId, dispatch, profileOverview.profile.profile_type_code, saveResultState.status]);


    return (
        <>
            <div className='full-width'>
                <div className='content-container'>
                    <ErrorMessages errors={errors} />
                    {(!profileAttributes.loading && !previousProfileAttributes.loading) &&
                        <div className='accordion-root'>
                            <div className={accordionDisplay(isInternal, "accordion")}>
                                <ControlledAccordion providerValue={providerValue}>
                                    <AccordionItem header={t('roomTableHeader')} key={'room-statistics-table'} itemKey={'room-statistics-table'}>
                                        <RoomStatisticsTab valueChangeHandler={handleRoomStatisticsChange} lookups={roomStatisticsFragmentData} data={roomData} />
                                    </AccordionItem>
                                </ControlledAccordion>
                            </div>
                            {shouldDisplayPreviousYearAttributes() ? <PreviousCampaignIncomeAttributes forcedTab lookups={roomStatisticsFragmentData} tabComponent='roomStatistics' year={String(getCurrentCampaignYear() - 1)} /> : <></>}
                            {isInternal && shouldDisplayPreviousTwoYearAttributes() ? <PreviousCampaignIncomeAttributes forcedTab lookups={roomStatisticsFragmentData} tabComponent='roomStatistics' year={String(getCurrentCampaignYear() - 2)} /> : <></>}
                        </div>
                    }
                </div>
            </div>
        </>
    )
}

export default RoomStatistics